import {
    withSiteBuilderBlockUuids,
} from "./siteBlockIdentity";
import {
    getSiteBuilderBlockDefinition,
} from "./siteBuilderSchema";
import {
    isSiteBuilderInnerBlockType,
    siteBuilderInnerBlockFootprint,
    siteBuilderTextColumnGridOptions,
    siteBuilderUsesTextColumn,
} from "./innerContentBlocks";
import { siteBuilderGeneratedFormKey } from "./siteBuilderForm";
import type { SiteBuilderBlock } from "./siteBuilderTypes";

export * from "./siteBlockIdentity";
export * from "./siteBuilderDocument";
export * from "./siteBuilderSchema";

function cloneBuilderRecord(
    value: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
    return value
        ? JSON.parse(JSON.stringify(value)) as Record<string, unknown>
        : undefined;
}

function cloneBuilderSlots(
    value: Record<string, SiteBuilderBlock[]> | undefined,
): Record<string, SiteBuilderBlock[]> | undefined {
    return value
        ? JSON.parse(JSON.stringify(value)) as Record<string, SiteBuilderBlock[]>
        : undefined;
}

/**
 * Low-level, target-neutral block factory. Web and Shop keep their own presets,
 * while editor UIs can create blocks from the shared current block schema.
 */
export function createSiteBuilderBlock(type: string): SiteBuilderBlock {
    if (type === "richContent" || type === "footer") {
        return withSiteBuilderBlockUuids({
            type,
            options: {
                ...(type === "footer"
                    ? { footerVariant: "columns" }
                    : {}),
                gridColumns: 12,
                gridRowHeight: 48,
            },
            slots: {
                grid: [],
                ...(type === "footer"
                    ? {
                        aboveDivider: [],
                        belowDivider: [],
                    }
                    : { content: [] }),
            },
        });
    }

    if (type === "column") {
        return withSiteBuilderBlockUuids({
            type,
            options: {
                gridH: 4,
                gridW: 6,
                gridX: 1,
                gridY: 1,
                stackOnMobile: true,
            },
            slots: {
                content: [],
            },
        });
    }

    const definition = getSiteBuilderBlockDefinition(type);
    const defaultSlots = cloneBuilderSlots(
        definition.contextualDefaults?.slots,
    );
    const slots = definition.zones || defaultSlots
        ? {
            ...(definition.zones
                ? Object.fromEntries(
                    definition.zones.map((zone) => [zone.key, []]),
                )
                : {}),
            ...(defaultSlots ?? {}),
        }
        : undefined;

    return withSiteBuilderBlockUuids({
        type,
        ...(cloneBuilderRecord(definition.contextualDefaults?.content)
            ? { content: cloneBuilderRecord(definition.contextualDefaults?.content) }
            : {}),
        ...(cloneBuilderRecord(definition.contextualDefaults?.options)
            ? { options: cloneBuilderRecord(definition.contextualDefaults?.options) }
            : {}),
        ...(slots ? { slots } : {}),
    });
}

export type SiteBuilderInsertionPlacement = "canvas" | "flow";

export type SiteBuilderInsertionOptions = {
    createBlock?: (type: string) => SiteBuilderBlock;
    editorInsertSource?: string;
    placement?: SiteBuilderInsertionPlacement;
};

function insertionFormIdentity(block: SiteBuilderBlock): SiteBuilderBlock {
    if (block.type !== "form") {
        return block;
    }

    const formKey = typeof block.content?.formKey === "string"
        ? block.content.formKey.trim()
        : "";

    if (formKey !== "") {
        return block;
    }

    const legacyPurpose = typeof block.content?.purpose === "string"
        ? block.content.purpose.trim()
        : "";

    if (legacyPurpose !== "") {
        return {
            ...block,
            content: {
                ...(block.content ?? {}),
                formKey: legacyPurpose,
            },
        };
    }

    const canonicalKey = siteBuilderGeneratedFormKey(block.uuid ?? "");

    return {
        ...block,
        content: {
            ...(block.content ?? {}),
            formKey: canonicalKey,
            purpose: canonicalKey,
        },
    };
}

function insertionFieldIdentity(block: SiteBuilderBlock): SiteBuilderBlock {
    if (block.type !== "field") {
        return block;
    }

    const authoredKey = typeof block.content?.name === "string"
        ? block.content.name.trim()
        : typeof block.content?.key === "string"
            ? block.content.key.trim()
            : "";

    if (authoredKey !== "") {
        return block;
    }

    return {
        ...block,
        content: {
            ...(block.content ?? {}),
            name: `field-${block.uuid}`,
        },
    };
}

/**
 * Resolves the document block type produced by a library or slash insertion.
 * Text-flow blocks use one movable Column on the canvas, while insertions into
 * an existing flow keep the authored block itself.
 */
export function siteBuilderInsertionBlockType(
    type: string,
    placement: SiteBuilderInsertionPlacement = "canvas",
): string {
    return placement === "canvas" && siteBuilderUsesTextColumn(type)
        ? "column"
        : type;
}

/**
 * Applies the canonical placement shape shared by Web and Shop editors.
 *
 * The supplied block already owns module-specific content and style defaults.
 * This helper owns only neutral canvas geometry and the movable text Column,
 * so adapters cannot silently diverge between sidebar and slash insertion.
 */
export function createSiteBuilderInsertionBlock(
    block: SiteBuilderBlock,
    {
        createBlock = createSiteBuilderBlock,
        editorInsertSource,
        placement = "canvas",
    }: SiteBuilderInsertionOptions = {},
): SiteBuilderBlock {
    const identifiedBlock = block.uuid?.trim()
        ? block
        : withSiteBuilderBlockUuids(block);
    const insertionBlock = insertionFieldIdentity(
        insertionFormIdentity(identifiedBlock),
    );

    if (placement === "flow") {
        return insertionBlock;
    }

    const footprint = isSiteBuilderInnerBlockType(insertionBlock.type)
        ? siteBuilderInnerBlockFootprint(insertionBlock.type)
        : null;

    if (!siteBuilderUsesTextColumn(insertionBlock.type)) {
        return footprint
            ? {
                ...insertionBlock,
                options: {
                    ...(insertionBlock.options ?? {}),
                    gridH: footprint.h,
                    gridW: footprint.w,
                },
            }
            : insertionBlock;
    }

    const createdColumn = createBlock("column");
    const column = createdColumn.uuid?.trim()
        ? createdColumn
        : withSiteBuilderBlockUuids(createdColumn);

    return {
        ...column,
        options: {
            ...(column.options ?? {}),
            ...(footprint
                ? {
                    gridH: footprint.h,
                    gridW: footprint.w,
                }
                : {}),
            ...siteBuilderTextColumnGridOptions(insertionBlock.type),
            alignment: "left",
            builderTextColumn: true,
            boxShadow: "none",
            ...(editorInsertSource ? { editorInsertSource } : {}),
            padding: "none",
            shape: "none",
            verticalAlign: "top",
        },
        slots: {
            ...(column.slots ?? {}),
            content: [insertionBlock],
        },
    };
}
